import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import Button from "../../../../component/button/Button";
import useVerifyPhoneCode from "../../../../query/useVerifyPhoneCode";
import { formatSecondsTimer, getSecondsLeftUntil } from "../../../../util/date";
import { getApolloErrorMessage } from "../../../../util/util";

import "./PhoneVerifyStep.css";

const CODE_LENGTH = 6;
const DEFAULT_RESEND_SECONDS = 300;

/**
 * PhoneVerifyStep
 *
 * 6자리 SMS 인증번호 입력 및 검증 UI.
 * 인증 성공 시 onSuccess(user) 를 호출하며, 부모는 그 user 로 로그인 처리.
 *
 * @param {Object} props
 * @param {string} props.phoneLastFour      휴대폰 뒷 4자리
 * @param {string} props.loginSessionToken  1단계에서 발급된 1회용 세션 토큰
 * @param {string} [props.expiresAt]        세션 만료 시각(ISO 문자열). 타이머/만료 판단에 사용
 * @param {boolean} [props.resending]       재전송 진행 중 여부 (부모 상태)
 * @param {() => void} props.onBack         이메일 입력 화면으로 복귀
 * @param {() => void} props.onResend       인증번호 재전송 요청
 * @param {(user: object) => void} props.onSuccess 인증 성공 시 사용자 객체 전달
 */
export default function PhoneVerifyStep({
  phoneLastFour,
  loginSessionToken,
  expiresAt,
  resending,
  resendError,
  onBack,
  onResend,
  onSuccess,
}) {
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState("");
  const [otpFocused, setOtpFocused] = useState(false);
  const initialSeconds = useMemo(
    () => getSecondsLeftUntil(expiresAt, DEFAULT_RESEND_SECONDS),
    [expiresAt, loginSessionToken],
  );
  const [secondsLeft, setSecondsLeft] = useState(initialSeconds);
  const otpRef = useRef(null);

  const [verifyPhoneCode, { called, data, loading, error }] =
    useVerifyPhoneCode();

  useEffect(() => {
    if (called && error) {
      setCodeError(getApolloErrorMessage(error) || "인증번호가 일치하지 않아요. 다시 확인해주세요.");
    }
  }, [called, error]);

  // 인증 성공 → 부모에 위임
  useEffect(() => {
    if (called && data?.verifySigninByEmail) {
      onSuccess(data.verifySigninByEmail);
    }
  }, [called, data]);

  // 카운트다운
  useEffect(() => {
    if (secondsLeft <= 0) return;
    const timer = setInterval(
      () => setSecondsLeft((s) => (s > 0 ? s - 1 : 0)),
      1000,
    );
    return () => clearInterval(timer);
  }, [secondsLeft]);

  // 새 세션 토큰이 발급되면(=재전송 성공) 타이머/입력 초기화
  useEffect(() => {
    setSecondsLeft(getSecondsLeftUntil(expiresAt, DEFAULT_RESEND_SECONDS));
    setCode("");
    setCodeError("");
  }, [loginSessionToken, expiresAt]);

  // 6자리 입력 완료시 자동 제출 처리
  useEffect(() => {
    if (code.length !== CODE_LENGTH) return;
    if (secondsLeft <= 0) return;
    if (loading) return;
    if (codeError) return;
    if (data?.verifySigninByEmail) return;
    verifyPhoneCode({
      variables: { loginSessionToken, verificationCode: code },
    });
  }, [code, secondsLeft, loading, codeError, data, loginSessionToken, verifyPhoneCode]);

  const onVerify = () => {
    if (code.length !== CODE_LENGTH) return;
    if (secondsLeft <= 0) {
      setCodeError("인증 시간이 만료되었어요. 인증번호를 다시 받아주세요.");
      return;
    }
    verifyPhoneCode({
      variables: { loginSessionToken, verificationCode: code },
    });
  };

  const handleResend = () => {
    setCodeError("");
    onResend();
    otpRef.current?.focus();
  };

  const preventFocusSteal = (e) => {
    e.preventDefault();
  };

  const onCodeChange = (e) => {
    const next = e.target.value.replace(/\D/g, "").slice(0, CODE_LENGTH);
    setCode(next);
    setCodeError("");
  };

  const onCodeKeyDown = (e) => {
    if (e.key === "Backspace" || e.key === "Delete") {
      e.preventDefault();
      setCode((prev) => prev.slice(0, -1));
      setCodeError("");
    }
  };

  const expired = secondsLeft <= 0;

  return (
    <div className="PhoneVerify-container">
      <button
        type="button"
        className="PhoneVerify-back-button"
        onClick={onBack}
      >
        ← 이메일 다시 입력
      </button>
      <h1 className="page-title">문자 인증</h1>
      <p className="PhoneVerify-desc">
        {`뒷자리 ${phoneLastFour || "****"}(으)로\n인증번호를 보내드렸어요`}
      </p>
      <div
        className={`PhoneVerify-otp ${codeError ? "is-error" : ""}`}
        onClick={() => otpRef.current?.focus()}
      >
        <div className="PhoneVerify-otp-cells">
          {Array.from({ length: CODE_LENGTH }).map((_, i) => {
            const active =
              otpFocused && code.length < CODE_LENGTH && i === code.length;
            return (
              <div
                key={i}
                className={`PhoneVerify-otp-cell${active ? " is-active" : ""}${code[i] ? " is-filled" : ""}`}
              >
                {code[i] ?? ""}
              </div>
            );
          })}
        </div>
        <input
          ref={otpRef}
          className="PhoneVerify-otp-input"
          value={code}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={CODE_LENGTH}
          onChange={onCodeChange}
          onKeyDown={onCodeKeyDown}
          onFocus={() => setOtpFocused(true)}
          onBlur={() => setOtpFocused(false)}
        />
      </div>
      {(codeError || resendError) ? (
        <p className="PhoneVerify-otp-error-text">{codeError || resendError}</p>
      ) : null}

      <div className="PhoneVerify-meta">
        <span className={expired ? "is-expired" : ""}>
          {expired
            ? "인증 시간 만료"
            : `남은 시간 ${formatSecondsTimer(secondsLeft)}`}
        </span>
        <button
          type="button"
          className="PhoneVerify-resend-button"
          onMouseDown={preventFocusSteal}
          onClick={handleResend}
          disabled={resending}
        >
          인증번호 재전송
        </button>
      </div>

      <div className="PhoneVerify-button-container">
        <Button
          onMouseDown={preventFocusSteal}
          onClick={onVerify}
          disabled={code.length !== CODE_LENGTH || loading}
          full={true}
          text={"인증하고 로그인"}
        />
      </div>
    </div>
  );
}

PhoneVerifyStep.propTypes = {
  phoneLastFour: PropTypes.string,
  loginSessionToken: PropTypes.string.isRequired,
  expiresAt: PropTypes.string,
  resending: PropTypes.bool,
  resendError: PropTypes.string,
  onBack: PropTypes.func.isRequired,
  onResend: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};
